/**
 * AvatarFolderPage
 *
 * Displays images inside an avatar folder.
 * Shows grid of generated avatars with actions.
 */

import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PostWriterSubmenuMemo } from '@/components/post-writer/PostWriterSubmenu';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, Plus, Upload } from 'lucide-react';
import { AVATAR_UPLOAD_CONSTANTS } from '@/@types/avatars';
import type { Id } from '../../convex/_generated/dataModel';
import { AvatarGridContent, DeleteAvatarDialog } from './AvatarFolderComponents';

export const AvatarFolderPage = () => {
  const navigate = useNavigate();
  const { folderId } = useParams<{ folderId: string }>();

  const folder = useQuery(
    api.avatarFolders.getFolderById,
    folderId ? { folderId: folderId as Id<'avatarFolders'> } : 'skip'
  );
  const avatars = useQuery(
    api.generatedAvatars.getAvatarsByFolder,
    folderId ? { folderId: folderId as Id<'avatarFolders'> } : 'skip'
  );

  const toggleReadyToUse = useMutation(api.generatedAvatars.toggleReadyToUse);
  const setAsDefault = useMutation(api.generatedAvatars.setAsDefault);
  const deleteAvatars = useMutation(api.generatedAvatars.deleteAvatars);

  const handleToggleReadyToUse = useCallback(
    async (avatarId: Id<'generatedAvatars'>) => {
      await toggleReadyToUse({ avatarId });
    },
    [toggleReadyToUse]
  );

  const handleSetAsDefault = useCallback(
    async (avatarId: Id<'generatedAvatars'>) => {
      await setAsDefault({ avatarId });
    },
    [setAsDefault]
  );

  const generateUploadUrl = useMutation(api.generatedAvatars.generateUploadUrl);
  const uploadAvatarMutation = useMutation(api.generatedAvatars.uploadAvatar);
  const [isFileUploading, setIsFileUploading] = useState(false);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !folderId) return;

      if (
        !AVATAR_UPLOAD_CONSTANTS.ACCEPTED_TYPES.includes(
          file.type as (typeof AVATAR_UPLOAD_CONSTANTS.ACCEPTED_TYPES)[number]
        )
      ) {
        alert('Please select a JPEG, PNG, or WebP image.');
        return;
      }

      if (file.size > AVATAR_UPLOAD_CONSTANTS.MAX_FILE_SIZE) {
        alert('File size must be under 20MB.');
        return;
      }

      setIsFileUploading(true);
      const url = URL.createObjectURL(file);
      const img = new window.Image();

      img.onerror = () => {
        URL.revokeObjectURL(url);
        setIsFileUploading(false);
        alert('Failed to load image. Please try a different file.');
      };

      img.onload = async () => {
        try {
          const uploadUrl = await generateUploadUrl();
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
          });

          if (!response.ok) throw new Error('Upload failed');

          const data: { storageId: string } = await response.json();

          await uploadAvatarMutation({
            storageId: data.storageId as Id<'_storage'>,
            folderId: folderId as Id<'avatarFolders'>,
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        } catch {
          alert('Failed to upload image. Please try again.');
        } finally {
          URL.revokeObjectURL(url);
          setIsFileUploading(false);
        }
      };

      img.src = url;
      e.target.value = '';
    },
    [folderId, generateUploadUrl, uploadAvatarMutation]
  );

  const [deletingAvatarId, setDeletingAvatarId] = useState<Id<'generatedAvatars'> | null>(null);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingAvatarId) return;
    await deleteAvatars({ avatarIds: [deletingAvatarId] });
    setDeletingAvatarId(null);
  }, [deletingAvatarId, deleteAvatars]);

  const isLoading = folder === undefined || avatars === undefined;

  if (folder === null) {
    return {
      submenu: <PostWriterSubmenuMemo />,
      mainContent: (
        <div className="h-full bg-background flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground">Folder not found</h2>
            <p className="text-muted-foreground mt-2">
              This folder doesn't exist or you don't have access.
            </p>
            <Button onClick={() => navigate('/ai-avatar/my-avatars')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to My Avatars
            </Button>
          </div>
        </div>
      ),
    };
  }

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent: (
      <div className="h-full bg-background overflow-auto">
        <div className="p-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/ai-avatar/my-avatars')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground">
                {isLoading ? 'Loading...' : folder?.title}
              </h1>
              {folder?.description && (
                <p className="text-sm text-muted-foreground mt-1">{folder.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="avatar-folder-file-upload" aria-label="Upload avatar image">
                <input
                  id="avatar-folder-file-upload"
                  type="file"
                  accept={AVATAR_UPLOAD_CONSTANTS.ACCEPTED_EXTENSIONS}
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isFileUploading}
                />
                <Button variant="outline" size="sm" disabled={isFileUploading} asChild>
                  <span>
                    {isFileUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {isFileUploading ? 'Uploading...' : 'Upload'}
                  </span>
                </Button>
              </label>
              <Button onClick={() => navigate('/ai-avatar/my-avatars/generate')} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Generate New
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
          ) : avatars.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="h-12 w-12 bg-muted rounded-lg flex items-center justify-center mb-4">
                <Plus className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground">No images in this folder</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                Generate new avatar images to add them to this folder.
              </p>
              <Button
                onClick={() => navigate('/ai-avatar/my-avatars/generate')}
                className="mt-4"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Generate Avatar
              </Button>
            </div>
          ) : (
            <AvatarGridContent
              avatars={avatars}
              onToggleReady={handleToggleReadyToUse}
              onSetDefault={handleSetAsDefault}
              onDelete={setDeletingAvatarId}
            />
          )}
        </div>

        <DeleteAvatarDialog
          avatarId={deletingAvatarId}
          onClose={() => setDeletingAvatarId(null)}
          onConfirm={handleDeleteConfirm}
        />
      </div>
    ),
  };
};
